import React, { useState, useEffect } from 'react';
import './VillageOfficials.css';

const API_BASE = '';

// Format phone number as (xxx) xxx-xxxx
const formatPhoneNumber = (phone) => {
  const cleaned = ('' + phone).replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

// Handle phone input formatting as user types
const handlePhoneInput = (e, setter, formSetter) => {
  const value = e.target.value;
  const key = e.nativeEvent.data;
  
  // Check if user pressed space after area code
  if (key === ' ' && value.match(/^\(\d{3}$/)) {
    // Add closing paren and space
    const formatted = value + ') ';
    setter(formatted);
    formSetter(prev => ({...prev, phone: formatted}));
    return;
  }
  
  // Remove all non-digits
  const cleaned = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limited = cleaned.slice(0, 10);
  
  // Format as user types
  let formatted = '';
  if (limited.length > 0) {
    if (limited.length <= 3) {
      formatted = `(${limited}`;
    } else if (limited.length <= 6) {
      formatted = `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    } else {
      formatted = `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  }
  
  setter(formatted);
  formSetter(prev => ({...prev, phone: formatted}));
};

// Validate email format
const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

function VillageOfficials({ isAdmin, authToken }) {
  const [officials, setOfficials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newOfficial, setNewOfficial] = useState({
    name: '',
    position: '',
    phone: '',
    email: ''
  });
  const [editForm, setEditForm] = useState({
    name: '',
    position: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    loadOfficials();
  }, []);

  const loadOfficials = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/officials`);
      const data = await response.json();
      // Sort by order field if exists
      const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0));
      setOfficials(sorted);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load officials:', error);
      setLoading(false);
    }
  };


  const handleAdd = async () => {
    if (!newOfficial.name || !newOfficial.position) {
      alert('Please provide name and position');
      return;
    }
    
    if (newOfficial.email && !validateEmail(newOfficial.email)) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      // Create official
      const formData = new FormData();
      formData.append('name', newOfficial.name);
      formData.append('position', newOfficial.position);
      formData.append('phone', newOfficial.phone || '');
      formData.append('email', newOfficial.email || '');
      formData.append('photo_url', '');
      formData.append('order', officials.length);
      
      const response = await fetch(`${API_BASE}/api/admin/officials`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${authToken}`
        },
        body: formData
      });

      if (response.ok) {
        setNewOfficial({ name: '', position: '', phone: '', email: '' });
        setShowAddForm(false);
        loadOfficials();
      } else {
        alert('Failed to add official');
      }
    } catch (error) {
      alert('Error adding official: ' + error.message);
    }
  };

  const handleUpdate = async (id) => {
    const official = officials.find(o => o.id === id);
    if (!editForm.name || !editForm.position) {
      alert('Please provide name and position');
      return;
    }
    
    if (editForm.email && !validateEmail(editForm.email)) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      // Update official
      const formData = new FormData();
      formData.append('name', editForm.name);
      formData.append('position', editForm.position);
      formData.append('phone', editForm.phone || '');
      formData.append('email', editForm.email || '');
      formData.append('photo_url', official.photo_url || '');
      formData.append('order', official.order || 0);
      
      const response = await fetch(`${API_BASE}/api/admin/officials/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${authToken}`
        },
        body: formData
      });

      if (response.ok) {
        setEditingId(null);
        setEditForm({ name: '', position: '', phone: '', email: '' });
        loadOfficials();
      } else {
        alert('Failed to update official');
      }
    } catch (error) {
      alert('Error updating official: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this official?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/admin/officials/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${authToken}`
        }
      });

      if (response.ok) {
        loadOfficials();
      } else {
        alert('Failed to delete official');
      }
    } catch (error) {
      alert('Error deleting official: ' + error.message);
    }
  };

  const handleMove = async (index, direction) => {
    const newOfficials = [...officials];
    const newIndex = index + direction;
    
    if (newIndex < 0 || newIndex >= officials.length) return;
    
    // Swap officials
    [newOfficials[index], newOfficials[newIndex]] = [newOfficials[newIndex], newOfficials[index]];
    
    // Update order values
    newOfficials.forEach((official, idx) => {
      official.order = idx;
    });
    
    setOfficials(newOfficials);

    // Save new order
    try {
      const formData1 = new FormData();
      formData1.append('name', newOfficials[index].name);
      formData1.append('position', newOfficials[index].position);
      formData1.append('phone', newOfficials[index].phone || '');
      formData1.append('email', newOfficials[index].email || '');
      formData1.append('photo_url', newOfficials[index].photo_url || '');
      formData1.append('order', index);
      
      const formData2 = new FormData();
      formData2.append('name', newOfficials[newIndex].name);
      formData2.append('position', newOfficials[newIndex].position);
      formData2.append('phone', newOfficials[newIndex].phone || '');
      formData2.append('email', newOfficials[newIndex].email || '');
      formData2.append('photo_url', newOfficials[newIndex].photo_url || '');
      formData2.append('order', newIndex);
      
      await Promise.all([
        fetch(`${API_BASE}/api/admin/officials/${newOfficials[index].id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Basic ${authToken}`
          },
          body: formData1
        }),
        fetch(`${API_BASE}/api/admin/officials/${newOfficials[newIndex].id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Basic ${authToken}`
          },
          body: formData2
        })
      ]);
    } catch (error) {
      console.error('Failed to save order:', error);
      loadOfficials();
    }
  };

  return (
    <div className="officials-page">
      <div className="page-header">
        <h1>Village Officials</h1>
        {isAdmin && (
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? 'Cancel' : '+ Add Official'}
          </button>
        )}
      </div>

      {/* Add Form */}
      {isAdmin && showAddForm && (
        <div className="add-official-form">
          <h3>Add New Official</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={newOfficial.name}
                onChange={(e) => setNewOfficial({...newOfficial, name: e.target.value})}
                placeholder="Full Name"
              />
            </div>
            <div className="form-group">
              <label>Position *</label>
              <input
                type="text"
                value={newOfficial.position}
                onChange={(e) => setNewOfficial({...newOfficial, position: e.target.value})}
                placeholder="e.g., Mayor, Village Clerk"
              />
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                value={newOfficial.phone}
                onChange={(e) => handlePhoneInput(e, (v) => {}, setNewOfficial)}
                placeholder="(217) 555-0123"
                maxLength="14"
              />
            </div>
            <div className="form-group">
              <label>Email (optional)</label>
              <input
                type="email"
                value={newOfficial.email}
                onChange={(e) => setNewOfficial({...newOfficial, email: e.target.value})}
                onBlur={(e) => {
                  if (e.target.value && !validateEmail(e.target.value)) {
                    alert('Please enter a valid email address');
                  }
                }}
                placeholder="email@thomasboro.us"
              />
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-success" onClick={handleAdd}>
              Add Official
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setShowAddForm(false);
                setNewOfficial({ name: '', position: '', phone: '', email: '' });
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Officials Grid */}
      {isAdmin && officials.length > 0 && (
        <div style={{textAlign: 'center', marginBottom: '20px', color: '#666'}}>
          <em>Use the arrow buttons to reorder officials by importance</em>
        </div>
      )}
      {loading ? (
        <div className="loading">Loading officials...</div>
      ) : (
        <div className="officials-grid">
          {officials.length === 0 ? (
            <p className="no-officials">No officials added yet.</p>
          ) : (
            officials.map((official, index) => (
              <div key={official.id} className="official-card">
                {editingId === official.id ? (
                  // Edit Mode
                  <div className="edit-mode">
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                      placeholder="Name"
                    />
                    <input
                      type="text"
                      value={editForm.position}
                      onChange={(e) => setEditForm({...editForm, position: e.target.value})}
                      placeholder="Position"
                    />
                    <input
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) => handlePhoneInput(e, (v) => {}, setEditForm)}
                      placeholder="(217) 555-0123"
                      maxLength="14"
                    />
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                      onBlur={(e) => {
                        if (e.target.value && !validateEmail(e.target.value)) {
                          alert('Please enter a valid email address');
                        }
                      }}
                      placeholder="Email (optional)"
                    />
                    <div className="edit-actions">
                      <button 
                        className="btn btn-sm btn-success"
                        onClick={() => handleUpdate(official.id)}
                      >
                        Save
                      </button>
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display Mode
                  <>
                    <div className="official-name">{official.name}</div>
                    <div className="official-position">{official.position}</div>
                    <div className="official-contact">
                      {official.email && (
                        <a href={`mailto:${official.email}`}>
                          {official.email}
                        </a>
                      )}
                      {official.phone && (
                        <a href={`tel:${official.phone.replace(/\D/g, '')}`}>
                          {formatPhoneNumber(official.phone)}
                        </a>
                      )}
                    </div>
                    {isAdmin && (
                      <div className="official-actions">
                        <button
                          className="btn-move"
                          onClick={() => handleMove(index, -1)}
                          disabled={index === 0}
                          title="Move to more important position"
                        >
                          ←
                        </button>
                        <button
                          className="btn-move"
                          onClick={() => handleMove(index, 1)}
                          disabled={index === officials.length - 1}
                          title="Move to less important position"
                        >
                          →
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            setEditingId(official.id);
                            setEditForm({
                              name: official.name,
                              position: official.position,
                              phone: official.phone || '',
                              email: official.email || ''
                            });
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(official.id)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default VillageOfficials;